import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import TextBox from "components/TextBox";
import ProjectTable from "./components/ProjectTable";
import ListBox from "components/ComboBox";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";
import Project from "models/ProjectBasicInfo";
import ProjectService from "api/Project/ProjectService";
import { AppRoutes } from "pages/constants/routes";
import PageSideBar from "components/PageSideBar";
import FinishDialog from "components/FinishDialog";
import LoadingIcon from "components/LoadingIcon";
import EmptyState from "./components/EmptyState";

import styles from "./Home.module.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const statusOptions: ComboBoxItem<number>[] = [
  { id: 0, value: 0, label: "Ready" },
  { id: 1, value: 1, label: "Initialising" },
  { id: 2, value: 2, label: "Error" },
  { id: 3, value: -1, label: "All" },
];

const Home = () => {
  const [projectData, setProjectData] = useState<Project[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [statusValue, setStatusValue] = useState<ComboBoxItem<number>>(
    statusOptions[3],
  );

  const [isWarningDialogOpen, setIsWarningDialogOpen] =
    useState<boolean>(false);
  const [projectToDelete, setProjectToDelete] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  const getProjects = async () => {
    try {
      setIsLoading(true);
      const response = await ProjectService.getUserProjects();
      setProjectData(
        response.projects.map(
          (project): Project => ({
            id: project.id,
            name: project.name,
            last_request_timestamp: project.last_request_timestamp,
            status: project.project_status,
            inference_mode: "off",
          }),
        ),
      );
    } catch (err) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProjects();
  }, []);

  const onDeleteClick = (project_id: string) => {
    setProjectToDelete(project_id);
    setIsWarningDialogOpen(true);
  };

  const onDeleteProject = async () => {
    try {
      await ProjectService.deleteProject(projectToDelete);
    } catch (err) {
      // TODO: Handle error
    } finally {
      getProjects();
    }
  };

  const onOpenProject = (proj_id: string) => {
    navigate(AppRoutes.project.replace(":proj_id", proj_id.toString()));
  };

  const renderEmptyState = () => <EmptyState />;

  const renderLoadingState = () => (
    <div className={styles.content}>
      <div className={styles.title}>Your Projects</div>
      <div className={styles.loadingBox}>
        <LoadingIcon />
        <div>Loading your projects</div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className={styles.content}>
      <div className={styles.title}>Your Projects</div>
      <div className={styles.tableHeader}>
        <div className={styles.controls}>
          <TextBox
            lean
            placeholder="Search..."
            value={searchValue}
            onChange={setSearchValue}
          />
          <div className={styles.filterContainer}>
            <span>Status</span>
            <ListBox
              lean
              className={styles.statusFilter}
              placeholder="All status"
              selected={statusValue}
              onChange={setStatusValue}
              options={statusOptions}
            />
          </div>
        </div>
        <button
          className={styles.createButton}
          onClick={() => navigate(AppRoutes.newProject)}
        >
          <span>Create</span>
          <span>+</span>
        </button>
      </div>
      <div className="ag-theme-quartz" style={{ height: 500 }}>
        <ProjectTable
          data={projectData}
          textFilter={searchValue}
          statusFilter={statusValue?.value}
          onDelete={onDeleteClick}
          onOpenProject={onOpenProject}
        />
      </div>

      {/* Warning Dialog for deleting Project */}
      <FinishDialog
        warning
        isOpen={isWarningDialogOpen}
        setIsOpen={setIsWarningDialogOpen}
        title="Warning"
        message="Your uploaded model and data will be permanently deleted"
        onConfirm={onDeleteProject}
      />
    </div>
  );

  return (
    <div className={styles.page}>
      <PageSideBar />
      {isLoading
        ? renderLoadingState()
        : projectData.length === 0
          ? renderEmptyState()
          : renderProjects()}
    </div>
  );
};

export default Home;
